import Immutable from 'immutable';
import cloneDeep from 'lodash/cloneDeep';
import intersection from 'lodash/intersection';

import {
  EDITABLE_CATEGORY_PROPERTY,
  MOVE_CATEGORY_DIRECTION,
  TEMP_CATEGORY_ID_PREFIX,
  TOP_LEVEL_CATEGORY_TITLE_BY_ID
} from 'app/constants/AdminBrowseConstants';

export const addEmptyCategory = (parentCategoryId, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryIndex = configCopy.categoryIndex;
  const parentCategory = categoryIndex[parentCategoryId];
  if (!parentCategory) {
    throw new Error(`No parent category found with categoryId: ${parentCategory}`);
  }

  const tempCategoryId = `${TEMP_CATEGORY_ID_PREFIX}${String(Date.now())}`;

  const emptyCategory = {
    categoryId: tempCategoryId,
    isEnabled: false,
    parentCategoryId,
    searchUrl: '',
    subCategoryIds: [],
    title: ''
  };

  categoryIndex[parentCategoryId].subCategoryIds.push(tempCategoryId);
  categoryIndex[tempCategoryId] = emptyCategory;

  return configCopy;
};

export const deleteCategoryFromConfig = (categoryId, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryIndex = configCopy.categoryIndex;
  const categoryToDelete = categoryIndex[categoryId];
  if (!categoryToDelete) {
    throw new Error(`${categoryId} not found in config`);
  }

  const { parentCategoryId } = categoryToDelete;
  const parentCategory = categoryIndex[parentCategoryId];
  if (!parentCategory) {
    throw new Error(`No parent category found with categoryId: ${parentCategory}`);
  }

  const { subCategoryIds = [] } = parentCategory;
  const indexOfChildCategoryId = subCategoryIds.indexOf(categoryId);
  const newSubCategoryIds = subCategoryIds
    .slice(0, indexOfChildCategoryId)
    .concat(subCategoryIds.slice(indexOfChildCategoryId + 1));

  categoryIndex[parentCategoryId].subCategoryIds = newSubCategoryIds;

  delete categoryIndex[categoryId];

  return configCopy;
};

export const getSubCategories = (categoryId, categoryConfig) => {
  const { categoryIndex } = categoryConfig;
  const parentCategory = categoryIndex[categoryId];
  if (!parentCategory) {
    return [];
  }

  return parentCategory.subCategoryIds.map(subCategoryId => categoryIndex[subCategoryId]);
};

export const hasAdminPermissions = user => {
  if (!user) {
    return false;
  }

  if (!Immutable.Map.isMap(user)) {
    throw new Error('user must be an Immutable.Map');
  }

  return intersection(['LIBRARY_STAFF', 'SYS_ADMIN'], user.get('roles').toArray()).length > 0;
};

export const isUnsavedCategory = category => {
  return category.categoryId.startsWith(TEMP_CATEGORY_ID_PREFIX);
};

export const makeCategoryConfigId = (subdomain, languageKey, topLevelCategoryId) => {
  const categoryId = topLevelCategoryId.substring(1); // remove initial `/`
  return `${subdomain}:${languageKey}:${categoryId}`;
};

export const makeEmptyCategoryConfig = (subdomain, languageKey, topLevelCategoryId) => {
  const categoryConfigId = makeCategoryConfigId(subdomain, languageKey, topLevelCategoryId);
  const title = TOP_LEVEL_CATEGORY_TITLE_BY_ID?.[topLevelCategoryId] ?? '';

  return {
    categoryConfigId,
    categoryIndex: {
      [topLevelCategoryId]: {
        categoryId: topLevelCategoryId,
        isEnabled: false,
        parentCategoryId: null,
        searchUrl: '',
        subCategoryIds: [],
        title
      }
    },
    languageKey,
    topLevelCategoryId,
    updatedAt: 0,
    updatedBy: ''
  };
};

export const moveCategoryPosition = (categoryId, moveCategoryDirection, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryIndex = configCopy.categoryIndex;
  const categoryToUpdate = categoryIndex[categoryId];
  if (!categoryToUpdate) {
    throw new Error(`${categoryId} not found in config`);
  }

  const { parentCategoryId } = categoryToUpdate;
  const parentCategory = categoryIndex[parentCategoryId];
  if (!parentCategory) {
    throw new Error(`No parent category found with categoryId: ${parentCategory}`);
  }

  const { subCategoryIds = [] } = parentCategory;
  const indexOfChildCategoryId = subCategoryIds.indexOf(categoryId);
  let newIndexOfChildCategoryId = indexOfChildCategoryId;

  if (moveCategoryDirection === MOVE_CATEGORY_DIRECTION.DOWN) {
    newIndexOfChildCategoryId = Math.min(subCategoryIds.length - 1, indexOfChildCategoryId + 1);
  } else if (moveCategoryDirection === MOVE_CATEGORY_DIRECTION.UP) {
    newIndexOfChildCategoryId = Math.max(0, indexOfChildCategoryId - 1);
  }

  const subCategoryIdsWithRemoval = subCategoryIds
    .slice(0, indexOfChildCategoryId)
    .concat(subCategoryIds.slice(indexOfChildCategoryId + 1));

  const newSubCategoryIds = [
    ...subCategoryIdsWithRemoval.slice(0, newIndexOfChildCategoryId),
    categoryId,
    ...subCategoryIdsWithRemoval.slice(newIndexOfChildCategoryId)
  ];

  categoryIndex[parentCategoryId].subCategoryIds = newSubCategoryIds;

  return configCopy;
};

export const serializeEditedCategoryConfig = (categoryConfig, updatedBy) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryIndex = configCopy.categoryIndex;

  Object.values(categoryIndex).forEach(category => {
    const isUnsaved = isUnsavedCategory(category);

    if (isUnsaved) {
      const oldCategoryId = category.categoryId;
      // TODO: this might need to be more robust to ensure a valid url slug
      // for example: should we enforce a character limit?
      const categorySlug = category.title
        .toLowerCase()
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9]/gi, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-*/, '')
        .replace(/-*$/, '');
      const newCategoryId = `${category.parentCategoryId || ''}/${categorySlug}`;

      category.categoryId = newCategoryId;

      if (category.parentCategoryId) {
        const parentCategory = categoryIndex[category.parentCategoryId];

        const indexOfCategory = parentCategory.subCategoryIds.indexOf(oldCategoryId);
        parentCategory.subCategoryIds[indexOfCategory] = newCategoryId;
      }

      delete categoryIndex[oldCategoryId];
      categoryIndex[newCategoryId] = category;
    }
  });

  configCopy.updatedAt = Date.now();
  configCopy.updatedBy = updatedBy;

  return configCopy;
};

export const toggleCategoryIsEnabled = (categoryId, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryIndex = configCopy.categoryIndex;
  const categoryToUpdate = categoryIndex[categoryId];
  if (!categoryToUpdate) {
    throw new Error(`${categoryId} not found in config`);
  }

  categoryToUpdate.isEnabled = !categoryToUpdate.isEnabled;

  return configCopy;
};

export const validateCategoryConfig = categoryConfig => {
  const { categoryIndex } = categoryConfig;
  const categoryIds = Object.keys(categoryIndex);

  return categoryIds.reduce((acc, categoryId) => {
    const category = categoryIndex[categoryId];
    const isTopLevelCategory = category.parentCategoryId === null;

    if (!isTopLevelCategory && !category.searchUrl) {
      acc.push(`[${categoryId}] Search URL is required`);
    }

    if (!category.title) {
      acc.push(`[${categoryId}] Title is required`);
    }

    return acc;
  }, []);
};

export const updateCategoryProperty = (categoryId, property, value, categoryConfig) => {
  if (!Object.values(EDITABLE_CATEGORY_PROPERTY).includes(property)) {
    throw new Error(`Unknown category property: ${property}`);
  }

  const configCopy = cloneDeep(categoryConfig);

  const categoryIndex = configCopy.categoryIndex;
  const categoryToUpdate = categoryIndex[categoryId];
  if (!categoryToUpdate) {
    throw new Error(`${categoryId} not found in config`);
  }

  categoryToUpdate[property] = value;

  return configCopy;
};
